import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ArrowUpRight, 
  ArrowDownLeft, 
  Calendar as CalendarIcon, 
  ReceiptText, 
  Wallet, 
  Bolt, 
  PlusCircle,
  TrendingUp,
  Zap,
  ChevronRight
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
} from 'recharts';
import { useState, useEffect } from 'react';
import { ViewType, Expense } from '../types';
import { formatAmount } from '@/lib/utils';


export default function Dashboard({ store, setView, onMonthFilter }: { store: any, setView: (v: ViewType) => void, onMonthFilter?: (year: number, month: number) => void }) {
  const { expenses, currentUser } = store;
  const [currentMonthLabel, setCurrentMonthLabel] = useState('');
  const [activeBarIndex, setActiveBarIndex] = useState<number | null>(null);
  const [isNarrowChart, setIsNarrowChart] = useState(false);

  useEffect(() => {
    setCurrentMonthLabel(new Date().toLocaleDateString('zh-TW', { year: 'numeric', month: 'long' }));
  }, []);

  useEffect(() => {
    const check = () => setIsNarrowChart(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Calculate balances
  let totalIOwe = 0;
  let totalOwedToMe = 0;

  expenses.forEach((exp: Expense) => {
    if (exp.payerId === currentUser?.id) {
      exp.participants.forEach(p => {
        if (p.userId !== currentUser.id && !p.isPaid) {
          totalOwedToMe += p.amountDue;
        }
      });
    } else {
      const myParticipation = exp.participants.find(p => p.userId === currentUser?.id);
      if (myParticipation && !myParticipation.isPaid) {
        totalIOwe += myParticipation.amountDue;
      }
    }
  });

  // Calculate monthly stats for the chart
  // ── 最新月份在最前（i=0 = 本月），不 reverse，方便直觀閱讀 ──
  const now = new Date();
  const thisYear = now.getFullYear();
  const thisMonth = now.getMonth() + 1; // 1-12

  const last12Months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1); // 先設 1 號，避免 Jan 31 → setMonth(-1) 溢位成 Jan 1
    d.setMonth(d.getMonth() - i);
    const mn = d.getMonth() + 1;
    const yr = d.getFullYear();
    return {
      month: `${mn}月`,
      monthNum: mn,
      year: yr,
      label: `${yr}年${mn}月`,
      total: 0,
      timestamp: d.getTime()
    };
  }); // 不 reverse：index 0 = 本月，index 11 = 11 個月前

  expenses.forEach((exp: Expense) => {
    // date-only string (e.g. "2026-05-01") 若直接 new Date() 會被解析為 UTC 00:00，
    // 在 UTC+8 時區會變成前一天 → 修正：手動以本地時區建立日期
    const rawDate = exp.expenseDate.slice(0, 10); // 取 "YYYY-MM-DD"
    const [ey, em, ed] = rawDate.split('-').map(Number);
    const d = new Date(ey, em - 1, ed); // 本地時區，不受 UTC 偏移影響
    const monthStr = `${d.getMonth() + 1}月`;
    const year = d.getFullYear();
    const monthData = last12Months.find(m => m.month === monthStr && m.year === year);
    if (monthData) {
      const myParticipation = exp.participants.find(p => p.userId === currentUser?.id);
      if (myParticipation) {
        monthData.total += myParticipation.amountDue;
      }
    }
  });

  // 本月總支出（index 0 = 本月）
  const currentMonthTotal = last12Months[0].total;

  // 本月支出筆數（只計算本月且為當前使用者有關的支出）
  const currentMonthExpenseCount = expenses.filter((exp: Expense) => {
    const rawDate = exp.expenseDate.slice(0, 10);
    const [ey, em] = rawDate.split('-').map(Number);
    if (ey !== thisYear || em !== thisMonth) return false;
    return exp.payerId === currentUser?.id || exp.participants.some((p: any) => p.userId === currentUser?.id);
  }).length;

  const recentExpenses = expenses.slice(0, 4);

  return (
    <div className="space-y-10">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight mb-1">您好，{currentUser?.nickname}</h2>
          <p className="text-xs md:text-base text-muted-foreground font-medium">家庭帳本最新狀態如下：</p>
        </div>
        <div className="hidden md:flex bg-muted px-4 py-2 rounded-xl items-center gap-2 border border-border">
          <span className="text-xs font-bold text-muted-foreground">{currentMonthLabel}</span>
          <CalendarIcon size={14} className="text-muted-foreground" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-primary p-6 md:p-8 rounded-2xl md:rounded-[2.5rem] text-primary-foreground flex flex-col justify-between min-h-[160px] md:min-h-[220px] shadow-wash relative overflow-hidden group cursor-pointer active:scale-[0.98] transition-transform"
          onClick={() => onMonthFilter?.(thisYear, thisMonth)}
        >
          <div className="absolute right-0 top-0 p-8 opacity-10 group-hover:scale-110 transition-transform">
            <TrendingUp size={120} />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-widest opacity-70 mb-1 block">本月總支出</span>
            <div className="text-3xl md:text-5xl font-black tracking-tighter">${formatAmount(currentMonthTotal)}</div>
          </div>
          <div className="flex items-center gap-2 text-primary-foreground/80 font-bold text-xs md:text-sm">
            <Zap size={14} className="text-accent md:w-4 md:h-4" />
            <span>目前累計支出筆數 {currentMonthExpenseCount}</span>
          </div>
        </Card>

        <Card
          className="bg-muted/30 p-6 md:p-8 rounded-2xl md:rounded-[2.5rem] border border-border/50 flex flex-col justify-between min-h-[160px] md:min-h-[220px] relative overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
          onClick={() => setView('settlements')}
        >
          <div className="absolute right-0 top-0 p-8 opacity-5">
            <ArrowUpRight size={120} />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1 block">待收回款項</span>
            <div className="text-3xl md:text-5xl font-black text-secondary tracking-tighter">${formatAmount(totalOwedToMe)}</div>
          </div>
          <div className="min-h-[44px] w-full flex justify-between items-center px-4 py-3 rounded-xl text-secondary font-bold gap-1 group text-xs md:text-sm">
            去結算 <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </div>
        </Card>

        <Card
          className="bg-muted/30 p-6 md:p-8 rounded-2xl md:rounded-[2.5rem] border border-secondary/20 flex flex-col justify-between min-h-[160px] md:min-h-[220px] relative overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
          onClick={() => setView('settlements')}
        >
          <div className="absolute right-0 top-0 p-8 opacity-5">
            <ArrowDownLeft size={120} />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1 block">待支付款項</span>
            <div className="text-3xl md:text-5xl font-black text-destructive tracking-tighter">${formatAmount(totalIOwe)}</div>
          </div>
          <div className="min-h-[44px] w-full flex justify-between items-center px-4 py-3 rounded-xl text-destructive font-bold gap-1 group text-xs md:text-sm">
            去結清 <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-12 gap-4 md:gap-8">
        <Card className="col-span-12 lg:col-span-8 bg-muted/20 p-4 md:p-8 rounded-2xl md:rounded-[2.5rem] border border-border/50 min-h-[400px]">
          <div className="flex items-center justify-between mb-4 md:mb-8">
            <h3 className="text-lg md:text-xl font-black text-primary flex items-center gap-3">
               <TrendingUp size={20} />
               年度支出走勢
            </h3>
            <div className="flex gap-2">
              <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-lg">
                <div className="w-3 h-3 rounded-full bg-primary"></div>
                <span className="text-xs font-bold text-primary">總額</span>
              </div>
            </div>
          </div>
          
          <div className="h-[280px] md:h-[300px] w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={last12Months} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.08)" />
                <XAxis 
                  dataKey="month" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 10, fill: '#888', fontWeight: 600 }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  width={48}
                  tick={{ fontSize: 10, fill: '#888', fontWeight: 600 }}
                  tickFormatter={(val) => `$${formatAmount(val)}`}
                />
                <Tooltip 
                  cursor={false}
                  contentStyle={{ borderRadius: '1rem', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}
                  labelFormatter={(_label: any, payload: any) => payload?.[0]?.payload?.label ?? _label}
                  formatter={(value) => {
                    if (value === undefined) {
                      return [`$0`, '總額'];
                    }
                    return [`$${formatAmount(value as number)}`, '總額'];
                  }}
                />
                <Bar
                  dataKey="total"
                  radius={[8, 8, 8, 8]}
                  barSize={isNarrowChart ? 16 : 32}
                  activeBar={false}
                  onClick={(_data: any, index: number) => {
                    if (activeBarIndex === index) {
                      const m = last12Months[index];
                      onMonthFilter?.(m.year, m.monthNum);
                      setActiveBarIndex(null);
                    } else {
                      setActiveBarIndex(index);
                    }
                  }}
                  shape={(props: any) => {
                    const { x, y, width, height, index } = props;
                    const isCurrent = index === 0; // index 0 = 本月（最左）
                    const isActive = index === activeBarIndex;
                    return (
                      <g style={{ cursor: 'pointer' }}>
                        <rect
                          x={x} y={y} width={width} height={height}
                          rx={8} ry={8}
                          fill={isCurrent ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                          fillOpacity={isCurrent ? 1 : 0.2}
                        />
                        {isActive && (
                          <rect
                            x={x - 3} y={y - 3} width={width + 6} height={height + 6}
                            rx={10} ry={10}
                            fill="none"
                            stroke="var(--color-primary)"
                            strokeWidth={2}
                          />
                        )}
                      </g>
                    );
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {activeBarIndex !== null && (
            <p className="text-xs text-center text-primary font-bold mt-3 animate-pulse">
              已選取 {last12Months[activeBarIndex].label}，再次點擊查詢該月支出
            </p>
          )}
        </Card>

        <div className="col-span-12 lg:col-span-4 space-y-6">
          <Card className="bg-muted border-border/50 rounded-[2rem] p-4 md:p-6 lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-2 h-2 bg-destructive rounded-full animate-pulse"></div>
              <h3 className="text-xl font-extrabold">快速捷徑</h3>
            </div>
            
            <div className="space-y-4">
              <Button 
                onClick={() => setView('add-expense')}
                className="w-full h-14 rounded-xl bg-primary text-primary-foreground font-bold flex gap-2"
              >
                <PlusCircle size={20} />
                新增一筆支出
              </Button>

              <Button 
                variant="outline"
                onClick={() => setView('accounts')}
                className="w-full h-14 rounded-xl border-border font-bold flex gap-2"
              >
                <Wallet size={20} />
                管理我的帳戶
              </Button>

              <div className="p-4 bg-primary/20 text-foreground rounded-xl relative overflow-hidden">
                <h5 className="font-bold text-sm relative z-10">功能提示</h5>
                <p className="text-xs mt-1 opacity-80 relative z-10 leading-relaxed">支出建立後，系統會自動通知其他參與成員，並在每週一發送付款提醒。</p>
                <div className="absolute -right-2 -bottom-2 opacity-10">
                  <Bolt size={64} />
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
